import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash';
import { ModeComment } from '@lendi-ui/icon';

import { EXPENSE_PROPERTY_RELATED_EXPENSES, EXPENSE_TYPE, FIELD_PATHS, FREQUENCY } from '@funnels/constants';
import { Expense, Expenses, FormValuesIncomesExpenses, ReduxStoreData } from '@funnels/types';
import {
  getAnnualValueFromFrequency,
  getApplicantName,
  getFieldValueLabel,
  getMarriedApplicantIds,
  groupExpensesByMaritalStatus,
  normaliseExpensesByFrequency,
} from '@funnels/utils';

import CommentsButton from '../../../Layouts/_common/CommentsButton';
import { ExpenseAmountFunnelReadOnlyView } from '../../../Questions/ExpenseAmount';
import { ExpenseOwnershipReadOnlyView } from '../../../Questions/ExpenseOwnership';
import { ExpenseTypeFunnelReadOnlyView } from '../../../Questions/ExpenseType';
import Table from '../../../Table';

const HEADERS = [
  { text: 'Category', min: 484, max: 961 },
  { text: 'Owner', min: 484, max: 667 },
  { text: 'Monthly Total', min: 144, max: 144 },
  { text: 'Annual Total', min: 144, max: 144 },
  { text: 'Comments', min: 144, max: 144 },
];

function mapState(currentState: ReduxStoreData) {
  const data: FormValuesIncomesExpenses = {
    applicants: currentState.applicants.data,
    incomes: currentState.incomes.data,
    expenses: currentState.expenses.data,
    assets: currentState.assets.data,
    employments: currentState.employments.data,
  };

  const error = currentState.applicants.error || currentState.incomes.error || currentState.expenses.error;
  const loading = currentState.applicants.loading + currentState.incomes.loading + currentState.expenses.loading;

  return { data, error, loading };
}

export interface ExpensesTableProps {
  handleShowModal: () => void;
}

function ExpensesTable(props: ExpensesTableProps) {
  const { data } = useSelector(mapState);
  const { rentInvestExpenses } = useFlags();
  const applicants = get(data, FIELD_PATHS.APPLICANTS(), []);

  const monthlyExpenses = normaliseExpensesByFrequency(data.expenses!, FREQUENCY.MONTHLY);
  const marriedApplicantIds = getMarriedApplicantIds(data.applicants);
  const expenses = groupExpensesByMaritalStatus(monthlyExpenses, marriedApplicantIds, { rentInvestExpenses });

  return (
    <Table
      title="Expenses"
      headers={HEADERS}
      after={
        expenses.length ? <CommentsButton handleShowModal={props.handleShowModal}>View comments</CommentsButton> : null
      }
    >
      {expenses.length > 0 && (
        <tbody data-component="table-tbody">
          {expenses.flatMap((_: Expenses, index: number) => {
            const applicantId = get({ expenses }, FIELD_PATHS.EXPENSES_APPLICANT_ID(index));
            const partnerApplicantId = get({ expenses }, FIELD_PATHS.EXPENSES_GROUPED_APPLICANT_ID(index));
            const ownerFullName = getApplicantName(applicants, applicantId);
            const partnerFullName = getApplicantName(applicants, partnerApplicantId);
            const ownersFullNames = partnerFullName ? [ownerFullName, partnerFullName] : [ownerFullName];
            const breakdownExpenses = get({ expenses }, FIELD_PATHS.EXPENSES_EXPENSES(index), []);

            return breakdownExpenses.map((_: Expense, expenseIndex: number) => {
              const expenseType = get({ expenses }, FIELD_PATHS.EXPENSES_EXPENSE_TYPE(index, expenseIndex));
              // don't display Estimated Living expenses
              if (expenseType === EXPENSE_TYPE.LIVING_EXPENSES_ESTIMATE) {
                return null;
              }
              const expenseTypeLabel = getFieldValueLabel(expenseType, expenseType);
              const expenseAmount = get({ expenses }, FIELD_PATHS.EXPENSES_EXPENSE_AMOUNT(index, expenseIndex));
              const annualTotal = getAnnualValueFromFrequency(expenseAmount, FREQUENCY.MONTHLY);
              const hasComment = get({ expenses }, FIELD_PATHS.EXPENSES_EXPENSE_COMMENTS(index, expenseIndex));

              const isPropertyRelatedExpense = EXPENSE_PROPERTY_RELATED_EXPENSES.includes(expenseType);

              if (isPropertyRelatedExpense && !expenseAmount) {
                return null;
              }

              return (
                <tr key={`expense-${index}-${expenseIndex}`}>
                  <td>
                    <ExpenseTypeFunnelReadOnlyView value={expenseTypeLabel} textSize="md" />
                  </td>
                  <td>
                    <ExpenseOwnershipReadOnlyView value={ownersFullNames} textSize="md" />
                  </td>
                  <td>
                    <ExpenseAmountFunnelReadOnlyView value={expenseAmount} textSize="md" />
                  </td>
                  <td>
                    <ExpenseAmountFunnelReadOnlyView value={annualTotal} textSize="md" />
                  </td>
                  <td>{hasComment && <ModeComment />}</td>
                </tr>
              );
            });
          })}
        </tbody>
      )}
    </Table>
  );
}

export default ExpensesTable;
